import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

interface ExplainBroadcastContentProps {
  markdown: string;
}

interface ExplainSection {
  heading: string;
  body: string;
}

const normalizeExplainMarkdown = (markdown: string): string => {
  let next = String(markdown || '').replace(/\r\n/g, '\n').trim();
  if (!next) return '';
  next = next.replace(/```(?:[\w+-]+)?\n([^\n`]{1,48})\n```/g, (_match, code) => `\`${String(code || '').trim()}\``);
  next = next.replace(/([^\n])\n(`[^`\n]{1,48}`)\n([^\n])/g, '$1 $2 $3');
  return next.replace(/\n{3,}/g, '\n\n').trim();
};

const parseExplainSections = (markdown: string): ExplainSection[] => {
  const text = normalizeExplainMarkdown(markdown);
  if (!text) return [];
  const lines = text.split('\n');
  const sections: ExplainSection[] = [];
  let currentHeading = '';
  let currentBody: string[] = [];

  const pushSection = () => {
    const body = currentBody.join('\n').trim();
    if (!currentHeading && !body) return;
    sections.push({
      heading: currentHeading || 'Explanation',
      body,
    });
  };

  lines.forEach((line) => {
    const headingMatch = line.match(/^##\s+(.+)$/);
    if (headingMatch) {
      if (currentHeading || currentBody.length > 0) pushSection();
      currentHeading = headingMatch[1].trim();
      currentBody = [];
      return;
    }
    currentBody.push(line);
  });
  pushSection();

  return sections.filter((section) => section.body.trim());
};

const explainSectionTone = (heading: string) => {
  const normalized = heading.toLowerCase();
  if (normalized.includes('final answer')) {
    return 'border-indigo-200 bg-indigo-50 text-slate-900';
  }
  if (normalized.includes('watch out') || normalized.includes('trap') || normalized.includes('miss') || normalized.includes('important note')) {
    return 'border-amber-200 bg-amber-50 text-amber-950';
  }
  if (normalized.includes('try this') || normalized.includes('quick check') || normalized.includes('check')) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-950';
  }
  if (normalized.includes('step') || normalized.includes('work through')) {
    return 'border-slate-200 bg-slate-50 text-slate-900';
  }
  return 'border-indigo-200 bg-white/80 text-slate-900';
};

const markdownComponents = {
  p({ children }: any) {
    return <p className="my-2 leading-relaxed">{children}</p>;
  },
  ul({ children }: any) {
    return <ul className="my-2 list-disc space-y-1 pl-6">{children}</ul>;
  },
  ol({ children }: any) {
    return <ol className="my-2 list-decimal space-y-1 pl-6">{children}</ol>;
  },
  li({ children }: any) {
    return <li className="leading-relaxed">{children}</li>;
  },
  strong({ children }: any) {
    return <strong className="font-semibold text-slate-950">{children}</strong>;
  },
  pre({ children }: any) {
    return <>{children}</>;
  },
  code({ inline, children, ...props }: any) {
    const raw = String(children || '').replace(/\n$/, '');
    const compactBlock = !inline && !raw.includes('\n') && raw.length <= 48;
    if (inline || compactBlock) {
      return (
        <code
          {...props}
          className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.92em] text-slate-900"
        >
          {raw}
        </code>
      );
    }
    return (
      <pre className="overflow-x-auto rounded-xl bg-slate-950 p-4 text-xs text-slate-100">
        <code {...props}>{raw}</code>
      </pre>
    );
  },
};

export const ExplainBroadcastContent: React.FC<ExplainBroadcastContentProps> = ({ markdown }) => {
  const normalized = useMemo(() => normalizeExplainMarkdown(markdown), [markdown]);
  const sections = useMemo(() => parseExplainSections(markdown), [markdown]);

  if (!normalized) return null;

  if (sections.length > 0) {
    return (
      <div className="space-y-3">
        {sections.map((section, index) => (
          <div
            key={`explain-section-${section.heading}-${index}`}
            className={`rounded-2xl border px-4 py-4 ${explainSectionTone(section.heading)}`}
          >
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em]">
              {section.heading}
            </div>
            <div className="mt-3 text-base">
              <ReactMarkdown components={markdownComponents}>
                {section.body}
              </ReactMarkdown>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-indigo-100 bg-white/85 px-4 py-4 text-base text-slate-900">
      <ReactMarkdown components={markdownComponents}>
        {normalized}
      </ReactMarkdown>
    </div>
  );
};
